<!--
This file is part of the project: "Digiziege"
An app for animal-welfare-oriented goat husbandry and contactless weight estimation.
GitLab: https://gitlab.rlp.net/proj-wise2526-digiziege/digiziege

Copyright (c) 2026 Digiziege Team TH-Bingen

Description:
Companion app for welfare-oriented goat husbandry featuring contactless weight estimation via camera,
master data management and health tracking.
Built as a PWA with a local-first approach to ensure data privacy.

LICENSE INFORMATION:
This program is free software: you can redistribute it and/or modify it under the terms of the
GNU Affero General Public License as published by the Free Software Foundation,
either version 3 of the License, or (at your option) any later version.

This program is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
See the GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License along with this program.
If not, see <https://www.gnu.org/licenses/>.
-->

<template>
  <div class="flex flex-col w-full">
    <div class="mb-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4 w-full">
      
      <div class="flex gap-2 w-full sm:w-auto items-center">
        <button
             @click="$emit('refresh')"
             :title="t('common.buttons.refresh')"
             class="h-[42px] w-[42px] shrink-0 flex items-center justify-center rounded-lg border border-[var(--color-primary-400)] bg-[var(--color-primary-200)] hover:bg-[var(--color-primary-300)] text-[var(--color-primary-600)] transition-colors"
             :class="{'animate-pulse pointer-events-none': loading}"
        >
            <RefreshCcw class="w-5 h-5" :class="{'animate-spin': loading}" />
        </button>

        <div class="relative w-full sm:w-[300px]">
          <input
            v-model="searchQuery"
            type="text"
            :placeholder="t('common.tableList.searchbar.placeholder')"
            class="input-field !pl-10 h-[42px] w-full min-w-0 rounded-lg border focus:ring-2 focus:outline-none transition-all"
            :style="{
                borderColor: 'var(--color-primary-400)',
                backgroundColor: 'var(--color-bg)',
                color: 'var(--color-text)'
            }"
          />
          <div class="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--color-primary-500)] pointer-events-none">
            <Search class="w-5 h-5" />
          </div>
          <button
            v-if="searchQuery"
            @click="searchQuery = ''"
            class="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--color-primary-500)] p-1 hover:text-[var(--color-primary-700)]"
          >
            <X class="w-4 h-4" />
          </button>
        </div>
      </div>

      <div class="flex gap-2 shrink-0 w-full sm:w-auto justify-end">
          <slot name="toolbar-actions"></slot>
      </div>
    </div>

    <hr class="border-[var(--color-primary-300)] mb-4 w-full" />

    <div class="w-full overflow-x-auto relative rounded-lg border border-[var(--color-primary-400)] shadow-sm bg-[var(--color-primary-100)] flex flex-col">
      
      <div v-if="loading" class="absolute inset-0 z-50 bg-[var(--color-primary-100)] bg-opacity-80 flex items-center justify-center min-h-[100px]">
        <div class="animate-spin w-8 h-8 border-4 border-[var(--color-primary-500)] border-t-transparent rounded-full"></div>
      </div>

      <table class="w-full border-collapse">
          <thead class="bg-[var(--color-primary-200)] border-b border-[var(--color-primary-400)]">
            <tr>
              <th
                v-for="(col, index) in columns"
                :key="col.key"
                @click="sortBy(col.key)"
                class="px-4 py-3 text-left text-xs font-bold text-[var(--color-primary-900)] uppercase tracking-wider cursor-pointer hover:bg-[var(--color-primary-300)] transition-colors select-none whitespace-nowrap"
                :class="{ 'text-center': col.center, 'text-right': col.right }"
              >
                <div class="flex items-center gap-1" :class="{ 'justify-center': col.center, 'justify-end': col.right }">
                  {{ col.label }}
                  <span v-if="sortKey === col.key" class="text-[var(--color-primary-600)]">
                    <ArrowUp v-if="sortOrder === 'asc'" class="w-3 h-3" />
                    <ArrowDown v-else class="w-3 h-3" />
                  </span>
                </div>
              </th>
            </tr>
          </thead>

          <tbody class="divide-y divide-[var(--color-primary-300)]">
            <tr
              v-for="item in processedData"
              :key="item.id"
              @click="$emit('row-click', item)"
              class="group cursor-pointer transition-colors duration-150 hover:bg-[var(--color-primary-200)]"
            >
              <td
                v-for="(col) in columns"
                :key="col.key"
                class="px-4 py-3 text-sm text-[var(--color-text)] font-medium whitespace-nowrap"
                :class="{ 'text-center': col.center, 'text-right': col.right }"
              >
                <slot :name="`cell-${col.key}`" :item="item" :value="item[col.key]">
                   {{ formatValue(item[col.key], col.type) }}
                </slot>
              </td>
            </tr>

            <tr v-if="processedData.length === 0 && !loading">
              <td :colspan="columns.length" class="px-6 py-8 text-center">
                <div class="flex flex-col items-center gap-2 text-[var(--color-primary-500)] opacity-70">
                    <SearchX class="w-8 h-8" />
                    <span class="text-sm font-medium">{{ t('common.tableList.noEntries') }}</span>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      
      <div v-if="processedData.length > 0" class="bg-[var(--color-primary-200)] px-4 py-1.5 text-[10px] text-[var(--color-primary-600)] border-t border-[var(--color-primary-400)] flex justify-end">
        <span>{{ processedData.length }} {{ t('common.tableList.Entries') }}</span>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { Search, X, ArrowUp, ArrowDown, RefreshCcw, SearchX } from 'lucide-vue-next'

const props = defineProps({
  data: { type: Array, required: true, default: () => [] },
  columns: { type: Array, required: true },
  loading: { type: Boolean, default: false },
})

const emit = defineEmits(['row-click', 'refresh'])
const { t } = useI18n()
const searchQuery = ref('')
const sortKey = ref(null)
const sortOrder = ref('asc') 

// Helpers
const formatDateInternal = (val) => {
    if (!val) return ''
    return new Date(val).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

const formatValue = (val, type) => {
    if (val === null || val === undefined) return '-'
    if (type === 'date') return formatDateInternal(val)
    if (type === 'number') return Number(val).toLocaleString('de-DE')
    return val
}

// Sorting logic
const sortBy = (key) => {
    if (sortKey.value === key) {
        sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc'
    } else {
        sortKey.value = key
        sortOrder.value = 'desc' 
    }
}
const processedData = computed(() => {
    let result = [...props.data]

    //Filter
    if (searchQuery.value) {
        const query = searchQuery.value.toLowerCase()
        result = result.filter(item => {
            return props.columns.some(col => {
                const val = item[col.key]
                if (val === null || val === undefined) return false
                if (col.type === 'date') return formatDateInternal(val).includes(query)
                return String(val).toLowerCase().includes(query)
            })
        })
    }

    //Sort
    if (sortKey.value) {
        result.sort((a, b) => {
            let valA = a[sortKey.value]
            let valB = b[sortKey.value]
            
            // Handle Dates and Numbers properly
            if (valA === null) return 1; if (valB === null) return -1;
            
            if (!isNaN(parseFloat(valA)) && !isNaN(parseFloat(valB))) {
                valA = parseFloat(valA)
                valB = parseFloat(valB)
            } else {
                valA = String(valA).toLowerCase()
                valB = String(valB).toLowerCase()
            }

            if (valA < valB) return sortOrder.value === 'asc' ? -1 : 1
            if (valA > valB) return sortOrder.value === 'asc' ? 1 : -1
            return 0
        })
    } else {
        // Default fallback sort: most recent Date if exists
        const dateCol = props.columns.find(c => c.type === 'date')
        if(dateCol) {
             result.sort((a, b) => new Date(b[dateCol.key]) - new Date(a[dateCol.key]))
        }
    }

    return result
})
</script>